import { HttpBackend, HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslateLoader, type TranslationObject } from '@ngx-translate/core';
import { Observable, from, timer } from 'rxjs';
import { retry, timeout } from 'rxjs/operators';
import { APP_VERSION_FULL } from 'public/environments/version';

import {
  APP_TRANSLATE_LOADER_CONFIG,
  type AppTranslateLoaderConfig,
} from './app-translate-loader.config';

function parseContentLength(response: HttpResponse<unknown>): number | null {
  const raw = response.headers.get('Content-Length');
  if (!raw) return null;

  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function buildContentRange(start: number, end: number, total: number): string {
  return `bytes ${start}-${end}/${total}`;
}

@Injectable({ providedIn: 'root' })
export class AppDictionaryFetcher {
  private readonly config = inject(APP_TRANSLATE_LOADER_CONFIG);
  private readonly rawHttp = new HttpClient(inject(HttpBackend));
  private readonly cache = new Map<string, Promise<TranslationObject>>();
  private readonly cacheBusterParams = new HttpParams().set('v', APP_VERSION_FULL);

  async load(lang: string): Promise<TranslationObject> {
    const normalizedLang = lang.trim();
    if (!normalizedLang) {
      throw new Error('Translation language is required.');
    }

    const cached = this.cache.get(normalizedLang);
    if (cached) return cached;

    const request = this.loadUncached(normalizedLang).catch((error: unknown) => {
      this.cache.delete(normalizedLang);

      if (error instanceof Error) {
        throw error;
      }

      throw new Error(`Unable to load translations for "${normalizedLang}".`);
    });

    this.cache.set(normalizedLang, request);
    return request;
  }

  private async loadUncached(lang: string): Promise<TranslationObject> {
    const url = this.buildUrl(lang);
    return this.config.mode === 'range'
      ? this.loadWithRange(url, lang)
      : this.loadWholeDictionary(url, lang);
  }

  private async loadWithRange(url: string, lang: string): Promise<TranslationObject> {
    const totalBytes = await this.resolveDictionaryLength(url);
    if (!totalBytes) {
      return this.loadWholeDictionary(url, lang);
    }

    const chunkSize = Math.max(1, this.config.rangeChunkSizeBytes);
    const chunks: Uint8Array[] = [];

    for (let start = 0; start < totalBytes; start += chunkSize) {
      const end = Math.min(totalBytes - 1, start + chunkSize - 1);
      const response = await this.performRequest(
        this.rawHttp.get(url, {
          observe: 'response',
          responseType: 'arraybuffer',
          params: this.cacheBusterParams,
          headers: new HttpHeaders({
            Range: `bytes=${start}-${end}`,
          }),
        }),
      );

      const body = new Uint8Array(response.body ?? new ArrayBuffer(0));

      if (response.status === 200) {
        if (start !== 0 || body.byteLength !== totalBytes) {
          throw new Error(
            `Range transport returned an unexpected full response for "${lang}".`,
          );
        }

        return this.parseDictionary(body, lang);
      }

      if (response.status !== 206) {
        throw new Error(
          `Range transport returned status ${response.status} for "${lang}".`,
        );
      }

      const expectedLength = end - start + 1;
      if (body.byteLength !== expectedLength) {
        throw new Error(
          `Range transport returned ${body.byteLength} bytes instead of ${expectedLength} for "${lang}".`,
        );
      }

      const contentRange = response.headers.get('Content-Range');
      if (contentRange && contentRange !== buildContentRange(start, end, totalBytes)) {
        throw new Error(`Range transport returned mismatched Content-Range for "${lang}".`);
      }

      chunks.push(body);
    }

    return this.parseDictionary(this.joinChunks(chunks, totalBytes), lang);
  }

  private async loadWholeDictionary(url: string, lang: string): Promise<TranslationObject> {
    const response = await this.performRequest(
      this.rawHttp.get(url, {
        observe: 'response',
        responseType: 'text',
        params: this.cacheBusterParams,
      }),
    );

    return this.parseDictionary(response.body ?? '', lang);
  }

  private async resolveDictionaryLength(url: string): Promise<number | null> {
    try {
      const response = await this.performRequest(
        this.rawHttp.request('HEAD', url, {
          observe: 'response',
          responseType: 'text',
          params: this.cacheBusterParams,
        }),
      );

      return parseContentLength(response);
    } catch {
      return null;
    }
  }

  private async performRequest<T>(request: Observable<T>): Promise<T> {
    return await new Promise<T>((resolve, reject) => {
      request
        .pipe(
          timeout({ first: this.config.requestTimeoutMs }),
          retry({
            count: this.config.maxRetries,
            delay: () => timer(this.config.retryDelayMs),
          }),
        )
        .subscribe({
          next: (value) => resolve(value),
          error: (error: unknown) => reject(error),
        });
    });
  }

  private parseDictionary(
    payload: Uint8Array | string,
    lang: string,
  ): TranslationObject {
    const text = typeof payload === 'string' ? payload : new TextDecoder().decode(payload);
    const parsed = JSON.parse(text) as unknown;

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error(`Translation payload for "${lang}" is not a dictionary.`);
    }

    return parsed as TranslationObject;
  }

  private joinChunks(chunks: Uint8Array[], totalBytes: number): Uint8Array {
    const result = new Uint8Array(totalBytes);
    let offset = 0;

    for (const chunk of chunks) {
      result.set(chunk, offset);
      offset += chunk.byteLength;
    }

    if (offset !== totalBytes) {
      throw new Error(`Translation payload assembled to ${offset} bytes instead of ${totalBytes}.`);
    }

    return result;
  }

  private buildUrl(lang: string): string {
    return `${this.config.prefix}${lang}${this.config.suffix}`;
  }
}

@Injectable()
export class AppTranslateLoader extends TranslateLoader {
  private readonly fetcher = inject(AppDictionaryFetcher);

  override getTranslation(lang: string): Observable<TranslationObject> {
    return from(this.fetcher.load(lang));
  }
}
